/**
 * Which signed-in account this tab uses (D-028), per product. Each tab keeps its own in
 * sessionStorage under `dz.<app>.account`, so two tabs can show two accounts side by side and
 * nothing outlives the tab. Apps send it with every API call (`X-Dugzi-Account`) and the server
 * checks it is one of this browser's signed-in accounts; unset, the server uses the most recently
 * signed-in account. The selection is also held in memory, so it still binds requests where
 * sessionStorage is unavailable, and `onTabAccountChange` tells listeners when it changes.
 * React-free, so the API client (`api.ts`) and its unit tests can use it without loading the
 * components.
 */
const selected = new Map<string, string | null>();
const listeners = new Map<string, Set<() => void>>();

export function onTabAccountChange(app: string, fn: () => void) {
  const set = listeners.get(app) ?? new Set<() => void>();
  listeners.set(app, set); set.add(fn);
  return () => { set.delete(fn); if (!set.size && listeners.get(app) === set) listeners.delete(app); };
}

export const tabAccount = {
  get(app: string): string | null {
    if (selected.has(app)) return selected.get(app) ?? null;
    try { return sessionStorage.getItem(`dz.${app}.account`); } catch { return null; }
  },
  set(app: string, id: string | null) {
    const previous = tabAccount.get(app);
    selected.set(app, id);
    if (previous !== id) for (const fn of [...(listeners.get(app) ?? [])]) fn();
    try { if (id) sessionStorage.setItem(`dz.${app}.account`, id); else sessionStorage.removeItem(`dz.${app}.account`); } catch { /* the in-memory selection still binds requests */ }
  },
  /** Arriving from sign-in or "open in a new tab" (`?account=`): adopt it and tidy the address bar. */
  adoptFromUrl(app: string) {
    const u = new URL(location.href);
    const id = u.searchParams.get('account');
    if (!id) return;
    tabAccount.set(app, id);
    u.searchParams.delete('account');
    history.replaceState(history.state, '', u.pathname + u.search + u.hash);
  },
};
